import './LearningModule.scss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServices } from 'index';
import { useParams } from 'react-router-dom';
import { LoadingPlaceholder } from '@trackman/web-shared-components';
import { ModuleDetailsHeader } from '../ModuleDetails/ModuleDetailsHeader/ModuleDetailsHeader';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { TableOfContents } from 'pages/TableOfContents/TableOfContents';
import { GetPremiumCTA } from 'components/PremiumCTA/GetPremiumCTA';
import { useState, useEffect } from 'react';
import { ContentsEntryType, LearningModuleLesson } from 'types/learningHub/moduleContents';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { CalculatorKind } from 'types/learningMaterials';
import { VideoLesson } from './TypeSpecificComponents/VideoLesson/VideoLesson';
import { CalculatorLesson } from './TypeSpecificComponents/CalculatorLesson/CalculatorLesson';
import { PictureLesson } from './TypeSpecificComponents/PictureLesson/PictureLesson';
import { TextLesson } from './TypeSpecificComponents/TextLesson/TextLesson';
import { ChapterTest } from './TypeSpecificComponents/ChapterTest/ChapterTest';
import { CertificationExam } from './TypeSpecificComponents/CertificationExam/CertificationExam';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import {
  getCertificationDescription,
  getInitialSelectedEntry,
  getLessonHeader,
  getSelectedCertification,
  getSelectedChapterTest,
  getSelectedLesson,
} from 'utilities/learningModuleHelpers';

export const LearningModule = () => {
  const [selectedEntry, setSelectedEntry] = useState<{ entryId: string; type: ContentsEntryType; chapterId?: string } | undefined>(
    undefined
  );

  const slug = useParams().slug ?? '';
  const service = useServices().learningHubService;
  const profileService = useServices().profileService;
  const queryClient = useQueryClient();

  const isUserMember = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    staleTime: Infinity,
  });

  const moduleDetails = useQuery({
    queryKey: ['moduleDetails', slug],
    queryFn: () => service.getModuleDetails(slug),
    enabled: !!slug,
    refetchOnWindowFocus: false,
  });

  const tableOfContents = useQuery({
    queryKey: ['moduleContents', moduleDetails.data?.id],
    queryFn: () => service.getModuleContents(moduleDetails.data?.id ?? ' '),
    enabled: !!moduleDetails.data?.id,
    refetchOnWindowFocus: false,
  });

  const markResourceAsDone = useMutation({
    mutationFn: (contentId: string) => service.markResourceAsDone(contentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['moduleContents', moduleDetails.data?.id] });
    },
  });

  useEffect(() => {
    if (tableOfContents.data && !selectedEntry) {
      const { entryId, type, chapterId, isCompleted } = getInitialSelectedEntry(tableOfContents.data?.chapters ?? []);
      setSelectedEntry({ entryId, type, chapterId });
      if (!isCompleted) {
        markResourceAsDone.mutate(entryId);
      }
    }
  }, [tableOfContents.data]);

  const selectEntry = (entryId: string, type: ContentsEntryType, isCompleted: boolean, chapterId?: string) => {
    setSelectedEntry({ entryId, type, chapterId });

    if (!isCompleted && type === 'lesson') {
      markResourceAsDone.mutate(entryId);
    }
  };

  return (
    <>
      {!moduleDetails.isPending && moduleDetails.data ? (
        <ModuleDetailsHeader
          title={moduleDetails.data.title}
          shortDescription={moduleDetails.data.shortDescription}
          navigationPath={`/LearningHub/Modules/${slug}`}
        ></ModuleDetailsHeader>
      ) : (
        <Skeleton variant='text' sx={{ margin: '1.5rem' }} />
      )}
      <div
        className={
          !moduleDetails.isPending && moduleDetails.data && !tableOfContents.isPending && tableOfContents.data
            ? 'learning-module-wrapper'
            : 'pending-learning-module-wrapper'
        }
      >
        {!moduleDetails.isPending && moduleDetails.data && !tableOfContents.isPending && tableOfContents.data ? (
          <>
            <div className='contents'>
              <TableOfContents
                contents={tableOfContents?.data}
                isInteractive={true}
                customClasses='contents-in-module'
                selectedEntryId={selectedEntry?.entryId}
                handleEntrySelection={(id, type, isCompleted, chapterId) => selectEntry(id, type, isCompleted, chapterId)}
              />
            </div>
            {selectedEntry && (
              <div className='learning-module-content'>
                <div className='title-wrapper'>
                  {selectedEntry.type === 'lesson' && (
                    <>
                      <Subtitle>
                        <>
                          {
                            getLessonHeader(
                              tableOfContents?.data.chapters ?? [],
                              selectedEntry?.chapterId ?? '',
                              selectedEntry?.entryId ?? ''
                            ).header
                          }
                        </>
                      </Subtitle>
                      <Paragraph>
                        <>
                          {
                            getLessonHeader(
                              tableOfContents?.data.chapters ?? [],
                              selectedEntry?.chapterId ?? '',
                              selectedEntry?.entryId ?? ''
                            ).subheader
                          }
                        </>
                      </Paragraph>
                    </>
                  )}
                  {selectedEntry.type === 'certificate' && (
                    <>
                      <p className='subtitle'>Certification exam</p>
                      <div className='certificate-title'>
                        {isUserMember.data ? (
                          getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                          'Completed' ? (
                            <></>
                          ) : (
                            <Medal />
                          )
                        ) : (
                          <Crown className='premium-test' />
                        )}
                        <Subtitle>
                          <>{getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.title}</>
                        </Subtitle>
                      </div>
                      {getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                      'Completed' ? (
                        <></>
                      ) : (
                        <Paragraph>
                          <>{getCertificationDescription(moduleDetails.data?.certificationTests ?? [], selectedEntry?.entryId ?? '')}</>
                        </Paragraph>
                      )}
                    </>
                  )}
                  {selectedEntry.type === 'chapterTest' && (
                    <>
                      <p className='subtitle'>Chapter test</p>
                      <div className='certificate-title'>
                        {isUserMember.data ? <></> : <Crown className='premium-test' />}
                        <Subtitle>
                          <>{getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.title}</>
                        </Subtitle>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  {selectedEntry?.type === 'chapterTest' && (
                    <ChapterTest
                      chapterTestId={getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.id ?? ''}
                      moduleId={moduleDetails.data?.id ?? ''}
                      isCompleted={
                        getSelectedChapterTest(tableOfContents.data?.chapters ?? [], selectedEntry?.chapterId ?? '')?.status === 'Completed'
                      }
                    />
                  )}
                  {selectedEntry?.type === 'certificate' && (
                    <CertificationExam
                      isCompleted={
                        getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.status ===
                        'Completed'
                      }
                      title={
                        getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.title ?? ''
                      }
                      certificationId={
                        getSelectedCertification(tableOfContents.data?.certifications ?? [], selectedEntry?.entryId ?? '')?.id ?? ''
                      }
                      moduleId={moduleDetails.data?.id ?? ''}
                    />
                  )}
                  {selectedEntry?.type === 'lesson' && (
                    <LessonComponent
                      content={getSelectedLesson(
                        tableOfContents.data?.chapters ?? [],
                        selectedEntry?.chapterId ?? '',
                        selectedEntry?.entryId ?? ''
                      )}
                    />
                  )}
                </div>
                <GetPremiumCTA />
              </div>
            )}
          </>
        ) : (
          <LoadingPlaceholder sx={{ width: '100%' }} />
        )}
      </div>
    </>
  );
};

type LessonProps = {
  content: LearningModuleLesson | undefined;
};

const LessonComponent = ({ content }: LessonProps) => {
  const extractText = () => {
    var span = document.createElement('span');
    span.innerHTML = content?.text ?? '';
    return span.textContent || span.innerText;
  };

  return (
    <>
      {content?.kind === 'Video' && <VideoLesson youTubeKey={content.youTubeKey ?? ''} title={content.title} />}
      {content?.kind === 'Calculator' && (
        <CalculatorLesson
          calculatorKind={content.calculatorKind as CalculatorKind}
          calculatorOptions={JSON.parse(content.calculatorOptions ?? '')}
        />
      )}
      {content?.kind === 'Text' && <TextLesson text={extractText()} />}
      {content?.kind === 'Picture' && (
        <div className='picture-lesson'>
          <PictureLesson imageUrl={(content.medias && content.medias[0]?.fileUrl) ?? ''} imageTitle={content.title} />
          <div>{extractText()}</div>
        </div>
      )}
    </>
  );
};
